// src/components/dashboards/add_patient_v2/hooks/useSubmitCard.ts
// -----------------------------------------------------------------------------
// useSubmitCard — per-card streaming submit + poll-to-completion lifecycle.
//
// Wraps a single PatientCardData submission via the unified bulk-upload
// endpoint (N=1 batch), then polls /patients/bulk-upload/status/{job_id}/
// at 2s intervals (matching V1's BulkUploadProgress cadence) until a terminal
// state. The card status transitions through:
//
//   idle → submitting → polling → succeeded | failed
//
// Joan, 2026-05-22: a card is NOT marked succeeded until the backend status
// returns `completed`. Mid-flight states render a loader inside the card.
//
// Design reference: cursor_logs/030 § 2026-05-22 per-card streaming creation.
// -----------------------------------------------------------------------------

import { useCallback, useRef, useEffect } from "react";
import api from "../../../../api/api";
import { uploadData } from "../../../../api/apiHelpers";
import logger from "utils/logger";
import { PatientCardData } from "../types";

interface PatientStatusEntry {
  patient_index: number;
  patient_name: string;
  status: "pending" | "processing" | "completed" | "failed" | "skipped";
  created_patient_id: number | null;
  error_message: string;
}

interface StatusPollResponse {
  status: "pending" | "processing" | "completed" | "partial" | "failed" | "cancelled";
  processed_patients: number;
  total_patients: number;
  created_patient_ids: number[];
  errors?: string[];
  patient_statuses?: PatientStatusEntry[];
}

const TERMINAL_STATUSES = new Set([
  "completed",
  "partial",
  "failed",
  "cancelled",
]);

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error || new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

export interface UseSubmitCardArgs {
  /** Updates the card's lifecycle state in the parent's cards array. */
  updateCard: (id: string, patch: Partial<PatientCardData>) => void;
  /**
   * Task A2 — when the onboarding tutorial is running, the patient created
   * by this form must be flagged `is_tutorial_patient` so the tutorial's
   * seed step (POST /tutorial/seed/{id}/) passes its server-side gate
   * (Patient.is_tutorial_patient=True). Strictly tutorial-gated: when false
   * the key is never sent and normal creation behaviour is unchanged.
   */
  tutorialActive?: boolean;
}

export function useSubmitCard({
  updateCard,
  tutorialActive = false,
}: UseSubmitCardArgs) {
  /** Active polling intervals keyed by card id, so unmount can clear them all. */
  const pollersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  useEffect(() => {
    const pollers = pollersRef.current;
    return () => {
      pollers.forEach((interval) => clearInterval(interval));
      pollers.clear();
    };
  }, []);

  const stopPolling = useCallback((cardId: string) => {
    const existing = pollersRef.current.get(cardId);
    if (existing) {
      clearInterval(existing);
      pollersRef.current.delete(cardId);
    }
  }, []);

  /**
   * Submit one card. The card must be in `idle` or `failed` state. POSTs a
   * single-entry batch to /patients/bulk-upload/start/, then polls the job
   * status every 2s until terminal.
   */
  const submitCard = useCallback(
    async (
      card: PatientCardData,
      environmentId: string,
      environmentIds?: string[],
    ): Promise<void> => {
      if (card.status === "submitting" || card.status === "polling") return;
      if (!card.name.trim()) {
        updateCard(card.id, { nameMissing: true });
        return;
      }

      stopPolling(card.id);
      updateCard(card.id, {
        status: "submitting",
        errorMessage: null,
        nameMissing: false,
      });

      try {
        const filesPayload = await Promise.all(
          card.files.map(async (f) => ({
            name: f.name,
            content: await fileToBase64(f),
          })),
        );

        const payload = {
          environment_id: environmentId,
          // Multi-env targeting (cursor_logs/043): every ticked env. The backend
          // validates each against the user and uses environment_id as primary.
          environment_ids:
            environmentIds && environmentIds.length ? environmentIds : undefined,
          patients: [
            {
              name: card.name.trim(),
              external_id: card.externalId.trim() || undefined,
              description: card.description.trim() || undefined,
              tabular_prompt_text: card.tabularPromptText || undefined,
              // A2: tutorial-gated flag so the seed step can target this patient.
              ...(tutorialActive ? { is_tutorial_patient: true } : {}),
              files: filesPayload,
            },
          ],
        };

        const resp = (await uploadData(
          "/patients/bulk-upload/start/",
          payload,
          "application/json",
        )) as { job_id: string };

        updateCard(card.id, { status: "polling", jobId: resp.job_id });

        // Begin polling.
        const interval = setInterval(async () => {
          try {
            const statusResp = await api.get(
              `/patients/bulk-upload/status/${resp.job_id}/`,
            );
            const data = statusResp.data as StatusPollResponse;

            if (TERMINAL_STATUSES.has(data.status)) {
              stopPolling(card.id);
              if (data.status === "completed") {
                const patientId = data.created_patient_ids?.[0] ?? null;
                updateCard(card.id, {
                  status: "succeeded",
                  createdPatientId:
                    patientId !== null ? String(patientId) : null,
                  errorMessage: null,
                });
              } else {
                const errMessage =
                  data.errors?.[0] || `Backend reported status: ${data.status}`;
                updateCard(card.id, {
                  status: "failed",
                  errorMessage: errMessage,
                });
              }
            }
          } catch (err) {
            logger.error("[useSubmitCard] Status poll failed", err);
          }
        }, 2000);

        pollersRef.current.set(card.id, interval);
      } catch (err: unknown) {
        stopPolling(card.id);
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error ||
          (err as Error)?.message ||
          "Submit failed";
        logger.error("[useSubmitCard] submit failed", err);
        updateCard(card.id, { status: "failed", errorMessage: message });
      }
    },
    [updateCard, stopPolling, tutorialActive],
  );

  /**
   * Submit MANY idle cards as a SINGLE bulk-upload job (the "Create all"
   * button), then stream per-card state back without leaving the page.
   *
   * Joan, 2026-06-04: pressing "Create all" used to redirect to the dashboard
   * and reset the form. Now we keep the one batch job (so mass-tabular imports
   * stay a single job + single notification — no per-card job fan-out) and poll
   * its per-patient `patient_statuses`, mapping each entry back to its card by
   * `patient_index`. The index is the card's position in `cardsToSubmit`, which
   * the backend echoes verbatim as BulkUploadPatientStatus.patient_index.
   *
   * Card lifecycle mirrors submitCard: idle → submitting → polling →
   * succeeded | failed. Returns the job_id (or null on a start-call failure)
   * so the caller can surface the submit-success snackbar / handle errors.
   *
   * Design reference: cursor_logs/036 §34 (stay-on-page after Create-all).
   */
  const submitBulk = useCallback(
    async (
      cardsToSubmit: PatientCardData[],
      environmentId: string,
      environmentIds?: string[],
    ): Promise<string | null> => {
      if (cardsToSubmit.length === 0) return null;

      // index → card id, fixed at submit time. The backend assigns
      // patient_index by position in the `patients` array, so this is the
      // authoritative mapping for the whole job's lifetime.
      const indexToCardId = cardsToSubmit.map((c) => c.id);

      cardsToSubmit.forEach((c) =>
        updateCard(c.id, {
          status: "submitting",
          errorMessage: null,
          nameMissing: false,
        }),
      );

      let jobId: string;
      try {
        const patients = await Promise.all(
          cardsToSubmit.map(async (card) => ({
            name: card.name.trim(),
            external_id: card.externalId.trim() || undefined,
            description: card.description.trim() || undefined,
            tabular_prompt_text: card.tabularPromptText || undefined,
            // A2: tutorial-gated flag so the seed step can target this patient.
            ...(tutorialActive ? { is_tutorial_patient: true } : {}),
            files: await Promise.all(
              card.files.map(async (f) => ({
                name: f.name,
                content: await fileToBase64(f),
              })),
            ),
          })),
        );

        const resp = (await uploadData(
          "/patients/bulk-upload/start/",
          {
            environment_id: environmentId,
            // Multi-env targeting (cursor_logs/043).
            environment_ids:
              environmentIds && environmentIds.length ? environmentIds : undefined,
            patients,
          },
          "application/json",
        )) as { job_id: string };
        jobId = resp.job_id;
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error ||
          (err as Error)?.message ||
          "Submit failed";
        logger.error("[useSubmitCard] bulk submit failed", err);
        // Roll the whole batch back to failed so the user can retry; the
        // caller also surfaces the error.
        cardsToSubmit.forEach((c) =>
          updateCard(c.id, { status: "failed", errorMessage: message }),
        );
        return null;
      }

      // Use the jobId as the poller key (a bulk job is shared across cards, so
      // we keep ONE interval rather than one-per-card).
      const pollerKey = `bulk:${jobId}`;
      cardsToSubmit.forEach((c) =>
        updateCard(c.id, { status: "polling", jobId }),
      );

      const interval = setInterval(async () => {
        try {
          const statusResp = await api.get(
            `/patients/bulk-upload/status/${jobId}/`,
          );
          const data = statusResp.data as StatusPollResponse;

          // Map each per-patient entry back to its card. Terminal per-patient
          // states finalise that card immediately; non-terminal ones stay in
          // `polling`.
          (data.patient_statuses || []).forEach((ps) => {
            const cardId = indexToCardId[ps.patient_index];
            if (!cardId) return;
            if (ps.status === "completed") {
              updateCard(cardId, {
                status: "succeeded",
                createdPatientId:
                  ps.created_patient_id !== null
                    ? String(ps.created_patient_id)
                    : null,
                errorMessage: null,
              });
            } else if (ps.status === "failed" || ps.status === "skipped") {
              updateCard(cardId, {
                status: "failed",
                errorMessage:
                  ps.error_message || `Backend reported status: ${ps.status}`,
              });
            }
          });

          if (TERMINAL_STATUSES.has(data.status)) {
            stopPolling(pollerKey);
            // Backstop: finalise any card still left in `polling` (e.g. the
            // job ended before its per-patient row flipped terminal). Fall
            // back to the job-level created_patient_ids / errors.
            const reported = new Set(
              (data.patient_statuses || []).map((p) => p.patient_index),
            );
            indexToCardId.forEach((cardId, idx) => {
              if (reported.has(idx)) {
                const ps = (data.patient_statuses || []).find(
                  (p) => p.patient_index === idx,
                );
                if (ps && (ps.status === "completed" || ps.status === "failed" || ps.status === "skipped")) {
                  return; // already finalised above
                }
              }
              const errMessage = data.errors?.[0];
              if (data.status === "completed") {
                updateCard(cardId, { status: "succeeded", errorMessage: null });
              } else {
                updateCard(cardId, {
                  status: "failed",
                  errorMessage:
                    errMessage || `Backend reported status: ${data.status}`,
                });
              }
            });
          }
        } catch (err) {
          logger.error("[useSubmitCard] bulk status poll failed", err);
        }
      }, 2000);

      pollersRef.current.set(pollerKey, interval);
      return jobId;
    },
    [updateCard, stopPolling, tutorialActive],
  );

  return { submitCard, submitBulk };
}
